import { useContext, useEffect, useState } from 'react';
import { ChatContextProvider } from '../../context/ChatContext';
import { formatTime, getInitialsFromName } from '../../utils/miscUtils';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCheckMobileScreen } from '../../hooks';
import { useAppSelector } from '../../store/store';
import { IPerson, selectPersonLoggedIn } from '../../store/slices/auth';
import { selectNotifications } from '../../store/slices/notifications';
import { toast } from 'react-toastify';
import P2PConnectionContextProvider, {
  P2PConnectionContext,
} from '../../context/P2PConnectionContext';
import Chat from '../Chat/Chat';
import StartVideoCall from './StartVideoCall';
import logo from '../../assets/images/new-logo.svg';
import styles from './VideoCall.module.scss';
import { useTranslation } from '../../hooks/useTranslations';

export default function VideoCall() {
  const [searchParams] = useSearchParams();
  let action = searchParams.get('action');

  const renderComponent = () => {
    if (action === 'initiate-call' || action === 'initiate-answer') {
      return <StartVideoCall />;
    }
    if (action === 'answer' || action === 'call') {
      return <VideoCallComponent />;
    }
    return <div>Not Found</div>;
  };

  return (
    <ChatContextProvider>
      <P2PConnectionContextProvider>{renderComponent()}</P2PConnectionContextProvider>
    </ChatContextProvider>
  );
}

function VideoCallComponent() {
  const {
    handleMic,
    handleCam,
    closeTheCall,
    remoteMedia,
    localMedia,
    localAudio,
    localVideo,
    stream,
    connectionState,
    peerConnection,
    socket,
    setConnectionState,
    setExternalIdentifier,
    externalIdentifier,
    handleIceCandidate,
    setPeerConnection,
  } = useContext(P2PConnectionContext);
  const { translations } = useTranslation();

  const { iceServerData } = useAppSelector<any>((state) => state.chat);

  // const loadingText = () => {
  //   let text: string = '';
  //   if (connectionState === '') {
  //     text = 'Waiting for the guest to join.';
  //   }
  //   if (connectionState === 'connecting') {
  //     text = 'Connecting...';
  //   }
  //   if (connectionState === 'no-sdp-offer') {
  //     text = 'No offer for joining the call.';
  //   }
  //   return text;
  // };
  const loadingText = () => {
    if (connectionState === '') {
      return translations('videoCall.waiting_guest');
    }
    if (connectionState === 'connecting') {
      return translations('videoCall.connecting');
    }
    if (connectionState === 'no-sdp-offer') {
      return translations('videoCall.no_offer');
    }
    return '';
  };

  const navigate = useNavigate();
  const location = useLocation();
  const sdp_offer = location.state;
  const candidate_offer = JSON.parse(localStorage.getItem('ice_candidate_offer') || '{}');
  const [timer, setTimer] = useState<number>(0);
  const [hideChat, setHideChat] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  let action = searchParams.get('action');
  let conversationId = searchParams.get('id');

  const notifications = useAppSelector(selectNotifications);
  const currentChatNotificaitons = notifications.filter(
    (el: any) => el?.conversation_id === conversationId
  );

  let isMobile = useCheckMobileScreen();
  const personLoggedIn = useAppSelector<IPerson>(selectPersonLoggedIn);
  const userProfile = {
    personId: parseFloat(personLoggedIn.person_external_id),
    ...personLoggedIn,
  };
  const nameInitials = getInitialsFromName(
    `${personLoggedIn?.person_firstname + ' ' + personLoggedIn?.person_lastname}`
  );

  const handleHideChat = () => {
    setHideChat((state) => !state);
  };

  const handleCloseCall = () => {
    closeTheCall(externalIdentifier);
  };

  const handleNegotiationNeeded = async (id: string, _peer: any) => {
    try {
      setExternalIdentifier(id);

      if (action === 'call') {
        const sdp = await _peer.createOffer();
        await _peer.setLocalDescription(sdp);
        const data = {
          type: 'S',
          message_external_identifier: id,
          conversation_id: conversationId,
          person_id: userProfile.personId,
          person_first_name: userProfile.person_firstname,
          person_last_name: userProfile.person_lastname,
          message_status: [],
          payload: sdp,
        };
        socket.emit('sdp_offer', data);
      }

      if (action === 'answer') {
        await _peer.setRemoteDescription(new RTCSessionDescription(JSON.parse(sdp_offer)));
        const answerSdp = await _peer.createAnswer();
        _peer.setLocalDescription(answerSdp);
        socket.emit('sdp_answer', {
          conversation_id: conversationId,
          payload: answerSdp,
        });

        candidate_offer.forEach((el: RTCIceCandidateInit | undefined) =>
          _peer.addIceCandidate(new RTCIceCandidate(el))
        );
      }
    } catch (error) {
      console.error('Error creating offer:', error);
    }
  };

  useEffect(() => {
    isMobile && handleHideChat();
    if (connectionState === 'connected') {
      const interval = setInterval(() => setTimer((timer) => timer + 1), 1000);
      return () => clearInterval(interval);
    }
  }, [connectionState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (iceServerData) {
      const initConnection = async () => {
        try {
          localMedia.current.srcObject = stream;
          let id = Number(new Date()).toString(36);

          const _peer = await new RTCPeerConnection({ iceServers: iceServerData });

          setTimeout(() => {
            // Add local stream to RTCPeerConnection
            stream.getTracks().forEach((track: any) => {
              _peer.addTrack(track, stream);
            });

            _peer.onnegotiationneeded = (e) => {
              handleNegotiationNeeded(id, _peer);
            };

            _peer.onicecandidate = (e: any) => {
              handleIceCandidate(e);
            };

            _peer.ontrack = (e: any) => {
              remoteMedia.current.srcObject = e.streams[0];
            };

            _peer.onconnectionstatechange = (e: any) => {
              if (_peer.connectionState === 'disconnected') {
                toast.error(translations('label.user_disconnected'), {
                  position: toast.POSITION.BOTTOM_RIGHT,
                });
                closeTheCall(id);
              }
              if (_peer.connectionState === 'failed') {
                toast.error(translations('label.user_disconnected'), {
                  position: toast.POSITION.BOTTOM_RIGHT,
                });
                closeTheCall(id);
              }
              setConnectionState(_peer.connectionState);
            };

            _peer.oniceconnectionstatechange = (e: any) => {
              console.log('iceConnectionState', _peer.iceConnectionState);
            };

            setPeerConnection(_peer);
          }, 2000);
        } catch (error) {
          console.error('Error accessing media devices:', error);
        }
      };
      initConnection();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (peerConnection && externalIdentifier) {
      // ****** SOCKET EVENTS ******
      socket.on('receive_sdp_answer', (data: { payload: RTCSessionDescriptionInit }) => {
        peerConnection.setRemoteDescription(new RTCSessionDescription(data.payload));
      });

      socket.on('receive_candidate_answer', (data: RTCIceCandidateInit | undefined) => {
        peerConnection.addIceCandidate(new RTCIceCandidate(data));
      });

      socket.on('response_close_sdp_offer', (data: any) => {
        peerConnection?.close();
        stream?.getTracks().forEach(function (track: any) {
          track.stop();
        });
        return navigate('/web/chat');
      });

      socket.on('error_close_sdp_offer', (err: any) => {
        console.log('error_close_sdp_offer', err);
      });
    }
  }, [peerConnection !== null, externalIdentifier !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.videoContainer}>
      <div className={styles.header}>
        <img src={logo} alt="ecocareLogo" />
      </div>
      {['no-sdp-offer', 'connecting', ''].includes(connectionState) && (
        <div id="cover-spin">{loadingText()}</div>
      )}
      <div className={styles.videoCallWrapper}>
        <div className={hideChat ? `${styles.mainSection} ${styles.full}` : styles.mainSection}>
          <div className={styles.remoteVideoWrapper}>
            <video
              ref={remoteMedia}
              className={
                remoteMedia ? `${styles.remoteVideo}` : `${styles.remoteVideo} ${styles.hide}`
              }
              autoPlay
              playsInline
            />
            <div
              className={
                remoteMedia ? `${styles.emptyState} ${styles.hide}` : `${styles.emptyState}`
              }
            >
              <div className={styles.avatar}>{nameInitials}</div>
            </div>
          </div>

          <div className={styles.localVideoWrapper}>
            <video
              ref={localMedia}
              className={
                localVideo ? `${styles.localVideo}` : `${styles.localVideo} ${styles.hide}`
              }
              autoPlay
              playsInline
              muted
            />
            <div
              className={
                localVideo ? `${styles.emptyState} ${styles.hide}` : `${styles.emptyState}`
              }
            >
              <div className={styles.avatar}>{nameInitials}</div>
            </div>
          </div>
        </div>
        <div
          className={
            hideChat ? `${styles.secondSection} ${styles.hideSection}` : styles.secondSection
          }
        >
          <Chat chatBodyStyle={{ height: 'calc(100vh - 405px)', maxHeight: 'none' }} />
        </div>
      </div>

      <div className={styles.actionsWrapper}>
        <div className={styles.timeStamp}>{formatTime(timer)}</div>
        <div className={styles.actions}>
          <button onClick={handleMic} className={`${styles.btn}`}>
            <i className={`${localAudio ? styles.micOnIcon : styles.micOffIcon}`} />
          </button>
          <button
            onClick={handleCam}
            className={localVideo ? `${styles.btn}` : `${styles.btn} ${styles.red}`}
          >
            <i className={localVideo ? styles.videoWhiteIcon : styles.videoOff} />
          </button>
          <button onClick={handleCloseCall} className={`${styles.btn} ${styles.red}`}>
            <i className={styles.endCallWhiteIcon} />
          </button>
        </div>
        <div className={styles.actions}>
          {currentChatNotificaitons.length > 0 && (
            <div className={styles.notificationIndicator}>{currentChatNotificaitons}</div>
          )}
          <button
            onClick={handleHideChat}
            className={hideChat ? styles.chatBtn : `${styles.chatBtn} ${styles.active}`}
          >
            <i className={styles.chatWhiteIcon} />
          </button>
        </div>
      </div>
    </div>
  );
}
